import axios from 'axios'
import type { AxiosError, AxiosResponse } from 'axios'
import { v4 } from 'uuid'
import { getToken } from '@/helpers/azureFieldwork'
import {
  AZURE_AUTH_ENABLED,
  BASIC_AUTH_SECRET,
  BASIC_AUTH_USER,
  NRM_BASIC_AUTH_SECRET,
  NRM_BASIC_AUTH_USER,
  NRM_API_BASE_URL,
  PLANT_BASIC_AUTH_SECRET,
  PLANT_BASIC_AUTH_USER,
  PLANT_API_BASE_URL,
  CAMS_API_BASE_URL,
  FMP_API_BASE_URL
} from '@/settingsFieldwork'
import router from '@/router'
import { isFMPRoute } from '@/router/FMPRoutes'
import { isNetworkRecoveryManagerRoute } from '@/router/NRMRoutes'
import { isPLANTRoute } from '@/router/PLANTRoutes'

/**
 * Get the current route name to determine which API and credentials to use
 */
function getCurrentRouteName (): string | symbol {
  return router.currentRoute.value.name || ''
}

/**
 * Get the API base URL based on the current route - Schedule Manager and Capacity Manager use the same API
 * but FMP has its own API - it was built before CAMS project
 * and NRM/PLANT have their own APIs - they were built later
 */
function getApiBaseUrl (): string {
  const currentRouteName = getCurrentRouteName()
  if (isFMPRoute(currentRouteName)) {
    return FMP_API_BASE_URL
  } else if (isNetworkRecoveryManagerRoute(currentRouteName)) {
    return NRM_API_BASE_URL
  } else if (isPLANTRoute(currentRouteName)) {
    return PLANT_API_BASE_URL
  } else {
    return CAMS_API_BASE_URL
  }
}

/**
 * Get the basic auth user based on the current route - NRM/PLANT have their own
 */
function getBasicAuthUser (): string {
  const currentRouteName = getCurrentRouteName()
  if (isNetworkRecoveryManagerRoute(currentRouteName)) {
    return NRM_BASIC_AUTH_USER
  } else if (isPLANTRoute(currentRouteName)) {
    return PLANT_BASIC_AUTH_USER
  } else {
    return BASIC_AUTH_USER
  }
}

/**
 * Get the basic auth secret based on the current route - NRM/PLANT have their own
 */
function getBasicAuthSecret (): string {
  const currentRouteName = getCurrentRouteName()
  if (isNetworkRecoveryManagerRoute(currentRouteName)) {
    return NRM_BASIC_AUTH_SECRET
  } else if (isPLANTRoute(currentRouteName)) {
    return PLANT_BASIC_AUTH_SECRET
  } else {
    return BASIC_AUTH_SECRET
  }
}

/**
 * Request interceptor - applied to all requests
 */
axios.interceptors.request.use(async (config) => {
  if (config.method === 'get') {
    config.data = {}
  }

  if (!config.headers) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    config.headers = {}
  }

  config.baseURL = getApiBaseUrl()

  // Unique transaction ID for each request
  // config.headers['Bearer'] = v4()
  config.headers['X-Transaction-Id'] = v4()
  if (!(config.data instanceof FormData)) {
    // NRM has a file upload submission using FormData
    // This is for DocketsList.vue only, no other places use FormData currently
    // So for that request, we check the data type and don't override the content type header
    config.headers['Content-Type'] = 'application/json; charset=utf-8'
  }

  config.auth = {
    username: getBasicAuthUser(),
    password: getBasicAuthSecret()
  }

  if (AZURE_AUTH_ENABLED) {
    const token = await getToken()
    config.headers.Bearer = `${token}`
  }

  return config
}, (error) => {
  return Promise.reject(error)
})

const responseSuccessHandler = async (response: AxiosResponse) => {
  // can do an extra work here on success if we need to, just a placeholder
  return response
}

const responseErrorHandler = async (error: AxiosError) => {
  // We can do generic error popup / takeover page here

  // const config = error.config as AxiosRequestConfig
  // if (error.response && error.response.status === 401) {
  //   // attempts to get and update cache with new tokens
  //   const token = await getTokenFromAzure()
  //
  //   // try the request again with new token
  //   if (token && error.config && error.config.headers) {
  //     error.config.headers.Bearer = token
  //     return axios(config)
  //   } else {
  //     redirectToLogin()
  //   }
  // }
  return Promise.reject(error)
}

// Response interceptor
axios.interceptors.response.use(
  async (response) => responseSuccessHandler(response),
  async (error) => responseErrorHandler(error))

export default axios
